/**
 * One day's openings for a session being placed by hand — the day's own calendar (what holds it,
 * and where the session would stand) beside the free whole hours it could take (`model/dayOpenings`).
 *
 * Shared by the series window's mover and by "Przełóż" (calendar-management R9: the reschedule
 * shows the chosen day's occupied and free slots). The day's reads are the week view's own cache
 * entries (`visibleRange('week', day)`), so a week the grid already showed answers at once. The
 * free hours are a native radio group — the arrows walk them — and picking one is `onPick`.
 * `aside` stands under the hours: the mover's conflict line, hint and "Gotowe".
 */

import { useQuery } from '@tanstack/react-query';
import { useId, type CSSProperties, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import hiddenControl from '~/shared/ui/visuallyHiddenControl.module.css';

import {
  trainerAvailabilityQueryOptions,
  trainerScheduleQueryOptions,
} from '../api/bookingQueries';
import { GRID_FIRST_HOUR } from '../config/gridGeometry';
import { bookingCalendar } from '../model/bookingCalendar';
import { visibleRange } from '../model/calendarRange';
import { dayOpenings, isOccupiedAt } from '../model/dayOpenings';
import { clockOf, wallReading, type CivilDay } from '../model/trainerClock';
import compose from './ComposeForm.module.css';
import styles from './SeriesDialog.module.css';

/** The day's calendar runs from the grid's opening hour to its evening. */
const DAY_OPEN = GRID_FIRST_HOUR * 60;
const DAY_CLOSE = 21 * 60;
const DAY_MINUTES = 1440;

interface DayOpeningsProps {
  readonly day: CivilDay;
  readonly zone: string;
  readonly nowMs: number;
  readonly durationMinutes: number;
  /** The session being moved — not in its own way. */
  readonly exceptId: number | null;
  /**
   * Where the session would stand that day; `null` for nowhere. `clash` when the caller knows (the
   * mover's occurrence carries it); otherwise it is read off the day's occupied sessions.
   */
  readonly candidate: { readonly minutes: number; readonly clash?: boolean } | null;
  readonly onPick: (minutes: number) => void;
  readonly aside?: ReactNode;
}

export function DayOpenings({
  day,
  zone,
  nowMs,
  durationMinutes,
  exceptId,
  candidate,
  onPick,
  aside,
}: DayOpeningsProps) {
  const { t } = useTranslation();
  const hourGroup = useId();
  const week = visibleRange('week', day);
  const events = useQuery(trainerScheduleQueryOptions(week));
  const hours = useQuery(trainerAvailabilityQueryOptions(week));
  const calendar =
    events.data === undefined || hours.data === undefined
      ? null
      : bookingCalendar({
          events: events.data,
          availability: hours.data,
          range: week,
          zone,
          nowMs,
        });
  const openings =
    calendar === null
      ? null
      : dayOpenings(day, {
          zone,
          nowMs,
          durationMinutes,
          working: calendar.working.get(day) ?? null,
          blocks: calendar.blocks,
          sessions: calendar.sessions,
          fallback: { start: DAY_OPEN, end: DAY_CLOSE },
          exceptId,
        });

  const candidateClash =
    candidate !== null &&
    (candidate.clash ??
      (openings !== null && isOccupiedAt(openings, day, candidate.minutes, durationMinutes, zone)));

  /** Where a span stands on the day's calendar, in hours from its opening. */
  const place = (startMinutes: number, minutes: number) =>
    ({
      '--at': String((startMinutes - DAY_OPEN) / 60),
      '--span': String(minutes / 60),
    }) as CSSProperties;

  return (
    <div className={styles.moverBody}>
      <div className={styles.dayCalendar} aria-busy={openings === null}>
        <div className={styles.dayHours} aria-hidden="true">
          {Array.from({ length: (DAY_CLOSE - DAY_OPEN) / 60 }, (_, index) => (
            <span key={index} className={styles.dayHour}>
              {clockOf(DAY_OPEN + index * 60)}
            </span>
          ))}
        </div>
        <ul className={styles.dayLane}>
          {(openings?.occupied ?? []).map((session) => (
            <li
              key={session.id}
              className={styles.dayOther}
              style={place(
                wallReading(session.startMs ?? 0, zone).minutes,
                session.durationMinutes,
              )}
            >
              {t('web:booking.series.mover.other', {
                time: session.startClock ?? '',
                name: session.clientName ?? t('web:booking.unnamedClient'),
              })}
            </li>
          ))}
          {candidate === null ? null : (
            <li
              className={`${String(styles.dayThis)} ${candidateClash ? String(styles.dayThisClash) : ''}`}
              style={place(candidate.minutes, durationMinutes)}
            >
              {`${clockOf(candidate.minutes)}–${clockOf((candidate.minutes + durationMinutes) % DAY_MINUTES)}`}
            </li>
          )}
        </ul>
        {openings === null ? (
          <p className={styles.dayState} role="status">
            {events.isError || hours.isError
              ? t('web:booking.series.mover.unread')
              : t('web:booking.series.mover.loading')}
          </p>
        ) : openings.occupied.length === 0 ? (
          <p className={styles.dayState}>{t('web:booking.series.mover.dayEmpty')}</p>
        ) : null}
      </div>

      <div className={styles.moverSide}>
        <fieldset className={compose.fieldset}>
          <legend className={compose.label}>{t('web:booking.series.mover.free')}</legend>
          {openings === null ? null : openings.free.length === 0 ? (
            <p className={styles.hint}>{t('web:booking.series.mover.noFree')}</p>
          ) : (
            <div className={styles.freeHours}>
              {openings.free.map((minutes) => (
                <label
                  key={minutes}
                  className={`${String(styles.freeHour)} ${minutes === candidate?.minutes ? String(styles.freeHourOn) : ''}`}
                >
                  <input
                    className={hiddenControl.control}
                    type="radio"
                    name={hourGroup}
                    checked={minutes === candidate?.minutes}
                    onChange={() => {
                      onPick(minutes);
                    }}
                  />
                  {clockOf(minutes)}
                </label>
              ))}
            </div>
          )}
        </fieldset>
        {aside}
      </div>
    </div>
  );
}
